import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import axios from 'axios';
import { ThemeProvider } from './contexts/ThemeContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { DmsProvider } from './contexts/DmsContext';
import DmsLayout from './layouts/DmsLayout';
import AdminRoute from './components/AdminRoute';
import DmsDashboard from './pages/DmsDashboard';
import VaultBrowser from './pages/VaultBrowser';
import DocumentDetail from './pages/DocumentDetail';
import SearchResults from './pages/SearchResults';
import AdminAudit from './pages/AdminAudit';
import AdminAnalytics from './pages/AdminAnalytics';
import WorkflowManager from './pages/WorkflowManager';
import TemplateBrowser from './pages/TemplateBrowser';
import ComplianceDashboard from './pages/ComplianceDashboard';
import CustomerPortal from './pages/CustomerPortal';
import UserManual from './components/Manual/UserManual';
import TechnicalManual from './components/Manual/TechnicalManual';
import DocumentEditorPage from './pages/DocumentEditorPage';
import useDmsPalette from './hooks/useDmsPalette';
import { decodeUserFromToken } from './utils/dmsPermissions';

const API_BASE = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

function LoginScreen({ onLogin }) {
  const MP = useDmsPalette();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/auth/login`, {
        email: email.toLowerCase().trim(),
        password,
      });
      if (data?.data?.token) {
        localStorage.setItem('token', data.data.token);
        onLogin(data.data.token);
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: MP.black }}>
      <Paper sx={{ p: 4, mx: 2, maxWidth: 400, width: '100%', bgcolor: MP.deepBlue, border: `1px solid ${MP.border}` }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src="/assets/images/logo.png" alt="SoldiKeeper" style={{ height: 56, marginBottom: 8 }} />
          <Typography variant="h5" fontWeight={700}>
            <span style={{ color: MP.blue }}>Soldi</span>
            <span style={{ color: MP.green }}>DMS</span>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enterprise Document Management
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <form onSubmit={handleSubmit}>
          <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required sx={{ mb: 2 }} autoFocus />
          <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required sx={{ mb: 3 }} />
          <Button fullWidth type="submit" variant="contained" size="large" disabled={loading} sx={{ py: 1.2 }}>
            {loading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}

function RoutedDashboard() {
  const navigate = useNavigate();
  return (
    <DmsDashboard
      onNavigate={(path) => navigate(path)}
      onDocumentSelect={(doc) => navigate(`/document/${doc._id}`)}
    />
  );
}

function RoutedVault() {
  const navigate = useNavigate();
  return (
    <VaultBrowser onDocumentSelect={(doc) => navigate(`/document/${doc._id}`)} />
  );
}

function RoutedSearch() {
  const navigate = useNavigate();
  return <SearchResults onDocumentSelect={(doc) => navigate(`/document/${doc._id}`)} />;
}

function RoutedTemplates() {
  const navigate = useNavigate();
  return <TemplateBrowser onDocumentSelect={(doc) => navigate(`/document/${doc._id}`)} />;
}

function RoutedCompliance() {
  const navigate = useNavigate();
  return <ComplianceDashboard onNavigate={(path) => navigate(path)} />;
}

function AppRoutes({ user, onLogout }) {
  return (
    <Routes>
      <Route element={<DmsLayout user={user} onLogout={onLogout} />}>
        <Route index element={<RoutedDashboard />} />
        <Route path="vault/:vaultId" element={<RoutedVault />} />
        <Route path="document/:id" element={<DocumentDetail />} />
        <Route path="search" element={<RoutedSearch />} />
        <Route path="customer" element={<CustomerPortal />} />
        <Route path="workflows" element={<WorkflowManager />} />
        <Route path="templates" element={<RoutedTemplates />} />
        <Route path="editor" element={<DocumentEditorPage />} />
        <Route path="editor/:documentId" element={<DocumentEditorPage />} />
        <Route path="audit" element={<AdminRoute user={user}><AdminAudit /></AdminRoute>} />
        <Route path="analytics" element={<AdminRoute user={user}><AdminAnalytics /></AdminRoute>} />
        <Route path="compliance" element={<AdminRoute user={user}><RoutedCompliance /></AdminRoute>} />
        <Route path="manual" element={<AdminRoute user={user} editorOnly><UserManual /></AdminRoute>} />
        <Route path="tech-manual" element={<AdminRoute user={user} editorOnly><TechnicalManual /></AdminRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const processToken = async (token) => {
    const payload = decodeUserFromToken(token);
    if (!payload) {
      localStorage.removeItem('token');
      setUser(null);
      setAuthChecked(true);
      return;
    }

    try {
      const { data } = await axios.get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const profile = data?.data;
      setUser({
        ...payload,
        ...profile,
        id: profile?._id || profile?.id || payload.id,
      });
    } catch {
      setUser(payload);
    }
    setAuthChecked(true);
  };

  useEffect(() => {
    const initAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const handoffCode = params.get('code');
      const legacyToken = params.get('token');

      if (handoffCode) {
        try {
          const { data } = await axios.post(`${API_BASE}/auth/dms-exchange`, { code: handoffCode });
          if (data?.token) {
            localStorage.setItem('token', data.token);
            window.history.replaceState({}, '', window.location.pathname);
            await processToken(data.token);
            return;
          }
        } catch {
          // fall through to login screen
        }
      }

      if (legacyToken) {
        localStorage.setItem('token', legacyToken);
        window.history.replaceState({}, '', window.location.pathname);
      }

      const token = localStorage.getItem('token');
      if (token) await processToken(token);
      else setAuthChecked(true);
    };

    initAuth();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (!authChecked) return null;
  if (!user) return <LoginScreen onLogin={processToken} />;

  return (
    <BrowserRouter>
      <AppRoutes user={user} onLogout={handleLogout} />
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <SnackbarProvider>
        <DmsProvider>
          <AppContent />
        </DmsProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}